import { useState } from 'react';
import ReactDataSheet from 'react-datasheet';
// Be sure to include styles at some point, probably during your bootstrapping
import 'react-datasheet/lib/react-datasheet.css';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as Yup from 'yup';

import { useSupportedLanguageIDs } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Heading } from '@treams/treams-ui';

import BulkDateField from './components/BulkDateField';
import BulkField from './components/BulkField';
import BulkSelect, {
	BulkSelectDepartment,
	BulkSelectJobTitle,
	useDepartmentOptions,
	useJobTitleOptions,
} from './components/BulkSelect';
import useGrid, { GridCell, GridRow } from './hooks/useGrid';
import { ISheetEditorEvents } from './types/sheet';

const Indexes = {
	firstName: 0,
	lastName: 1,
	username: 2,
	email: 3,
	activatedAt: 4,
	locale: 5,
	externalReferenceID: 6,
	departmentID: 7,
	jobPositionStartDate: 8,
	jobTitleID: 9,
	readyForUpload: 10,
	status: 11,
};

function createValidators(languageIDs: string[]): Yup.StringSchema[] {
	return [
		Yup.string().min(1).required(),
		Yup.string().min(1).required(),
		Yup.string().min(1).required(),
		Yup.string().email().required(),
		Yup.string(),
		Yup.string().oneOf(languageIDs).required(),
		Yup.string(),
		Yup.string().required(),
		Yup.string().required(),
		Yup.string().when('$jobTitleID', {
			is: (jobTitleID: string) => !!jobTitleID,
			then: (schema: Yup.StringSchema) => schema.required(),
			otherwise: (schema: Yup.StringSchema) => schema,
		}),
		Yup.string(),
	];
}

const validateRow = (row: GridRow, languageIDs: string[]) => {
	const validators = createValidators(languageIDs);

	return validators.forEach((validator, index) => {
		if (index === Indexes.jobPositionStartDate) {
			validator.validateSync(row[index].value, {
				context: { jobTitleID: row[Indexes.jobTitleID].value },
			});
		} else {
			validator.validateSync(row[index].value);
		}
	});
};

const isRowValid = (row: GridRow, languageIDs: string[]) => {
	try {
		validateRow(row, languageIDs);
	} catch {
		return false;
	}
	return true;
};

const userFromRow = (row: GridRow): graphql.CreateUserInput => {
	const username = row[Indexes.username].value!.split('@')[0];

	const activatedAt = row[Indexes.activatedAt].value?.trim();
	const externalReferenceID = row[Indexes.externalReferenceID].value?.trim();

	const user: graphql.CreateUserInput = {
		firstName: row[Indexes.firstName].value!,
		lastName: row[Indexes.lastName].value!,
		username,
		email: row[Indexes.email].value!,
		locale: row[Indexes.locale].value!,
		groupID: row[Indexes.departmentID].value ?? '',
		...(activatedAt ? { localActivatedAt: activatedAt.slice(0, 10) } : {}),
		...(externalReferenceID ? { externalReferenceID } : {}),
	};

	return user;
};

const renderCellAtIndex = (
	index: number,
	value: string,
	events: ISheetEditorEvents,
	languageIDs: string[],
) => {
	const validators = createValidators(languageIDs);
	if (index === Indexes.locale) {
		return (
			<BulkSelect
				options={languageIDs.map((languageID) => ({ label: languageID, value: languageID }))}
				value={value}
				events={events}
				validationSchema={validators[index] ?? undefined}
			/>
		);
	}

	if (index === Indexes.activatedAt) {
		return (
			<BulkDateField
				value={value}
				events={events}
				validationSchema={validators[index] ?? undefined}
			/>
		);
	}

	if (index === Indexes.departmentID) {
		return <BulkSelectDepartment value={value} events={events} />;
	}

	if (index === Indexes.jobPositionStartDate) {
		return (
			<BulkDateField
				value={value}
				events={events}
				validationSchema={validators[index] ?? undefined}
			/>
		);
	}

	if (index === Indexes.jobTitleID) {
		return <BulkSelectJobTitle value={value} events={events} />;
	}

	return (
		<BulkField events={events} value={value} validationSchema={validators[index] ?? undefined} />
	);
};

const ScrollElement = styled.div`
	overflow-x: scroll;
`;

const ScrollInnerElement = styled.div`
	width: 2000px;
`;

interface ISheetEditorProps extends ISheetEditorEvents {
	col: number;
	row: number;
	value: string | number | null | undefined;
}

function BulkEmployeeEdit() {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [create] = graphql.useCreateUserMutation();
	const [createJobPosition] = graphql.useCreateJobPositionMutation();
	const [grid, setGrid] = useGrid();

	const { languageIDs } = useSupportedLanguageIDs();

	const [, processDepartmentValue] = useDepartmentOptions();
	const [, processJobTitleValue] = useJobTitleOptions();

	const processValueAtIndex = (index: number, value: string): string | undefined => {
		if (index === Indexes.departmentID) {
			return processDepartmentValue(value);
		}

		if (index === Indexes.jobTitleID) {
			return processJobTitleValue(value);
		}

		return value;
	};

	const applyToRow = (row: number, column: number, value: string) => {
		const gridCopy = [...grid];
		gridCopy[row][column] = { ...gridCopy[row][column], value };
		setGrid(gridCopy);
	};

	const applyStatusToRow = (row: number, status: string) => applyToRow(row, Indexes.status, status);

	const startBulkCreate = async () => {
		const successMessage = 'Success';

		setIsLoading(true);

		for (let i = 1; i < grid.length; i += 1) {
			try {
				validateRow(grid[i], languageIDs);
			} catch (err) {
				applyStatusToRow(i, err.toString());
				continue;
			}

			const user = userFromRow(grid[i]);

			try {
				const createResponse = await create({
					variables: {
						input: user!,
					},
				});

				const createdUser = createResponse.data?.createUser.user;

				if (createdUser && grid[i][Indexes.jobTitleID].value) {
					await createJobPosition({
						variables: {
							input: {
								jobTitleID: grid[i][Indexes.jobTitleID].value ?? '',
								startDate: grid[i][Indexes.jobPositionStartDate].value,
								userID: createdUser.id,
							},
						},
					});
				}

				applyStatusToRow(i, successMessage);
			} catch {
				applyStatusToRow(i, 'Server error.');
			}
		}

		// Remove created users from array.
		setGrid(grid.filter((row) => row[Indexes.status].value !== successMessage));

		setIsLoading(false);
	};

	const onCellsChanged = (changes: ReactDataSheet.CellsChangedArgs<GridCell>) => {
		const updatedGrid = grid.map((row: GridRow) => [...row]);

		// Apply changes to existing cells
		changes.forEach(({ row, col, value }) => {
			updatedGrid[row][col] = {
				...updatedGrid[row][col],
				value: processValueAtIndex(col, value!),
			};
		});

		const changedRows = Array.from(new Set(changes.map((change) => change.row)));
		changedRows.forEach((row) => {
			updatedGrid[row][Indexes.readyForUpload] = {
				value: isRowValid(updatedGrid[row], languageIDs) ? 'Ja' : 'Nee',
				readOnly: true,
			};
		});

		setGrid(updatedGrid);
	};

	return (
		<Box p={3}>
			<Flex mb={4} justifyContent="space-between">
				<Heading>Bulk user upload</Heading>
				<Button
					small
					disabled={isLoading}
					onClick={() => startBulkCreate()}
					title="Save all changes"
				/>
			</Flex>

			<ScrollElement>
				<ScrollInnerElement>
					<ReactDataSheet
						disablePageClick
						data={grid}
						valueRenderer={(cell: GridCell) => cell.value}
						onCellsChanged={onCellsChanged}
						dataEditor={({ col, value, onChange, onRevert, onCommit }: ISheetEditorProps) => {
							return renderCellAtIndex(
								col,
								value as string,
								{
									onChange,
									onRevert,
									onCommit,
								},
								languageIDs,
							);
						}}
					/>
				</ScrollInnerElement>
			</ScrollElement>
		</Box>
	);
}

export default BulkEmployeeEdit;
